import { useCallback, useState } from 'react';
import { useApiCall } from '@/hooks/useApiCall';
import { useApiClient } from '@/hooks/useApiClient';
import type { LabelingExperiment } from '../types/LabelingTypes';
import {
  labelizeListExperiments,
  labelizeCreateSession,
  labelizeListSessions,
} from '../api/LabelingApiClient';

export interface UseExperimentSelectorResult {
  experiments: LabelingExperiment[];
  selectedExpId: string | null;
  selectedToolId: string | null;
  isLoading: boolean;
  error: string | null;
  setSelectedExpId: (id: string | null) => void;
  setSelectedToolId: (id: string | null) => void;
  retry: () => void;
  startLabeling: (expId: string) => Promise<{ toolId: string; sessionId: string }>;
}

export function useExperimentSelector(): UseExperimentSelectorResult {
  const http = useApiClient();

  const [experiments, setExperiments] = useState<LabelingExperiment[]>([]);
  const [selectedExpId, setSelectedExpId] = useState<string | null>(null);
  const [selectedToolId, setSelectedToolId] = useState<string | null>(null);

  const fetchExperiments = useCallback(
    async (signal: AbortSignal) => {
      if (!http) {
        return {
          ok: false as const,
          code: 500,
          name: 'InitializationError',
          message: 'HttpClient not initialized',
          timestamp: new Date().toISOString(),
        };
      }
      try {
        const data = await labelizeListExperiments(http, signal);
        return {
          ok: true as const,
          data: { experiments: data, timestamp: new Date().toISOString() },
          timestamp: new Date().toISOString(),
        };
      } catch (err) {
        return {
          ok: false as const,
          code: 500,
          name: 'FetchError',
          message: err instanceof Error ? err.message : 'Failed to list experiments',
          timestamp: new Date().toISOString(),
        };
      }
    },
    [http]
  );

  const { isLoading, error, retry } = useApiCall({
    apiFn: fetchExperiments,
    deps: [http],
    immediate: !!http,
    onSuccess: (data) => {
      if ('experiments' in data && Array.isArray(data.experiments)) {
        setExperiments(data.experiments as LabelingExperiment[]);
      }
    },
    onError: () => {
      setExperiments([]);
    },
  });

  const startLabeling = useCallback(
    async (expId: string): Promise<{ toolId: string; sessionId: string }> => {
      // Get-or-create: reuse an existing session if one exists (avoids a noisy 409
      // from creating a duplicate 'default' session), otherwise create it.
      const existing = await labelizeListSessions(http!, expId, undefined);
      if (existing.length > 0) {
        return { toolId: expId, sessionId: existing[0].sessionId };
      }
      const session = await labelizeCreateSession(http!, expId, undefined);
      return { toolId: expId, sessionId: session.sessionId };
    },
    [http]
  );

  return {
    experiments,
    selectedExpId,
    selectedToolId,
    isLoading,
    error,
    setSelectedExpId,
    setSelectedToolId,
    retry,
    startLabeling,
  };
}

// Backwards-compat alias for ToolSelector.tsx
export { useExperimentSelector as useToolSelector };
